import {
  isArchScopedItem,
  ItemTypeEnum,
  materialCharacteristicsWithoutShadeSchema,
  ORDERED_ARCHES,
  type DeepPartial,
  type TeethArch,
} from "@jasper/client-models";
import {
  isSplintItemType,
  SplintItemTypeEnum,
  SplintTypeForm,
  validateSchema,
  type SplintCharacteristics,
  type SplintItemType,
} from "@jasper/forms";
import { useItemMaterialsByItemType } from "@jasper/use-cases";
import { Alert, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useRegisterStepSave } from "./useRegisterStepSave";

const SPLINT_ITEM_TYPES = Object.values(SplintItemTypeEnum);

const getSplintCharacteristicsSchema = ({
  isMaterialRequired,
  arches,
}: {
  isMaterialRequired: boolean;
  arches: TeethArch[];
}) =>
  z
    .object({
      type: z.enum(SPLINT_ITEM_TYPES),
      materialCharacteristics:
        materialCharacteristicsWithoutShadeSchema.optional(),
      isMaterialPerArch: z.boolean().optional(),
      materialCharacteristicsByArch: z
        .partialRecord(
          z.enum(ORDERED_ARCHES),
          materialCharacteristicsWithoutShadeSchema.optional()
        )
        .optional(),
    })
    .superRefine((value, ctx) => {
      if (!isMaterialRequired || value.type !== ItemTypeEnum.nightGuard) {
        return;
      }

      if (value.isMaterialPerArch) {
        arches
          .filter(
            arch => !value.materialCharacteristicsByArch?.[arch]?.material
          )
          .forEach(arch =>
            ctx.addIssue({
              code: "custom",
              path: ["materialCharacteristicsByArch", arch, "material"],
            })
          );
        return;
      }

      if (!value.materialCharacteristics?.material) {
        ctx.addIssue({
          code: "custom",
          path: ["materialCharacteristics", "material"],
        });
      }
    });

type SplintFormValue = DeepPartial<SplintCharacteristics> | undefined;

export const SplintTypeStep = ({
  stepKey,
  products,
  orderConfiguration,
  registerStepSave,
}: ProductStepProps) => {
  const { isLoading: isOrderLoading, updateProducts } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.splintType",
  });

  const [product] = products;

  const { isLoading: isMaterialsLoading, itemMaterials: nightGuardMaterials } =
    useItemMaterialsByItemType({ itemType: ItemTypeEnum.nightGuard });

  const itemMaterials = useMemo(
    () =>
      nightGuardMaterials.map(({ value, label }) => ({
        value,
        label,
        itemType: ItemTypeEnum.nightGuard as SplintItemType,
      })),
    [nightGuardMaterials]
  );

  const archItems = useMemo(
    () => products.flatMap(({ items }) => items.filter(isArchScopedItem)),
    [products]
  );

  const arches = useMemo(
    () =>
      ORDERED_ARCHES.filter(arch => archItems.some(item => item.arch === arch)),
    [archItems]
  );

  const splintCharacteristicsSchema = useMemo(
    () =>
      getSplintCharacteristicsSchema({
        isMaterialRequired: itemMaterials.length > 0,
        arches,
      }),
    [itemMaterials.length, arches]
  );

  const validateSelection = ({
    value,
  }: {
    value: DeepPartial<SplintCharacteristics> | undefined;
  }) => {
    const result = validateSchema(splintCharacteristicsSchema, value);
    return result.success ? undefined : result.errors;
  };

  const persistedItem = product?.items.find(item =>
    isSplintItemType(item.type)
  );

  const defaultValues = useMemo(() => {
    const materialByArch = new Map(
      archItems.flatMap(item =>
        item.materialCharacteristics?.material
          ? [[item.arch, item.materialCharacteristics.material] as const]
          : []
      )
    );
    const [sharedMaterial] = materialByArch.values();
    const isMaterialPerArch = new Set(materialByArch.values()).size > 1;

    return {
      splint: {
        type: persistedItem?.type,
        materialCharacteristics: sharedMaterial
          ? { material: sharedMaterial }
          : undefined,
        isMaterialPerArch,
        materialCharacteristicsByArch: Object.fromEntries(
          [...materialByArch].map(([arch, material]) => [arch, { material }])
        ),
      } as DeepPartial<SplintCharacteristics> | undefined,
    };
  }, [persistedItem, archItems]);

  const getItemMaterialCharacteristics = (
    splint: SplintFormValue,
    item: ConfigurableProduct["items"][number]
  ) => {
    const arch = isArchScopedItem(item) ? item.arch : undefined;
    const material =
      splint?.isMaterialPerArch && arch && arches.length > 1
        ? splint.materialCharacteristicsByArch?.[arch]?.material
        : splint?.materialCharacteristics?.material;

    return splint?.type === ItemTypeEnum.nightGuard && material
      ? { material }
      : undefined;
  };

  const hasChangesToSave = (splint: SplintFormValue) =>
    products.some(configuredProduct =>
      configuredProduct.items.some(
        item =>
          item.type !== splint?.type ||
          item.materialCharacteristics?.material !==
            getItemMaterialCharacteristics(splint, item)?.material
      )
    );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine ||
          !hasChangesToSave(formApi.state.values.splint)
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine ||
          !hasChangesToSave(formApi.state.values.splint)
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const type = value.splint?.type;

      if (!type) {
        return;
      }

      const buildItems = (configuredProduct: ConfigurableProduct) =>
        configuredProduct.items.map(item => ({
          ...item,
          type,
          materialCharacteristics: getItemMaterialCharacteristics(
            value.splint,
            item
          ),
        }));

      try {
        await updateProducts(
          products.map(configuredProduct => ({
            productId: configuredProduct.id,
            items: buildItems(configuredProduct),
          }))
        );
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset(defaultValues);
    }
  }, [product, defaultValues, form]);

  if (isOrderLoading || isMaterialsLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">{t("productNotFound")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field
          name="splint"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const errors = validateSelection({ value });

              if (!errors) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return errors;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <SplintTypeForm
                onBlur={() => field.handleBlur()}
                value={field.state.value}
                errors={formErrors}
                onChange={next => field.setValue(next)}
                itemMaterials={itemMaterials}
                arches={arches}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
